import { Calendar, ExternalLink, Linkedin, Mail, Search } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { getMyProspects } from "../../interfaces/prospect";
import { getMyInfo } from "../../interfaces/user";
import ProspectHeatmap from "views/home/components/ProspectHeatmap";
import { generateEmailSearchUrl, EmailProvider } from "../../utils/emailProviders";
import { Input } from "../../components/ui/Input";
import { Badge } from "../../components/ui/Badge";
import { Select } from "../../components/ui/Select";

type LogRow = {
    id: string;
    prospectId?: string;
    prospectName?: string;
    type: string; // e.g. send-email, receive-email, linkedin-send, meeting
    subject?: string;
    message?: string;
    date?: string;
    prospectEmail?: string;
    prospectLinkedin?: string;
};

const formatDate = (iso?: string) => {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return iso;
    }
};

export default function AllLogs() {
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<LogRow[]>([]);
    const [q, setQ] = useState("");
    const [typeFilter, setTypeFilter] = useState<string>("all");
    const [emailProvider, setEmailProvider] = useState<EmailProvider>("gmail");

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const res: any = await getMyProspects();
                if (cancelled) return;
                const data = Array.isArray(res?.data || res) ? (res?.data || res) : [];
                const collected: LogRow[] = [];
                for (const p of data) {
                    const logs = Array.isArray(p.logs) ? p.logs : [];
                    for (const [i, l] of logs.entries()) {
                        collected.push({
                            id: `${p._id || p.id}-${i}`,
                            prospectId: p._id || p.id,
                            prospectName: `${p.firstName || ""} ${p.lastName || ""}`.trim() || "Prospect",
                            type: l.type || "",
                            subject: l.subject,
                            message: l.message,
                            date: l.date || l.createdAt || undefined,
                            prospectEmail: p.email,
                            prospectLinkedin: p.linkedinUrl,
                        });
                    }
                }
                // sort desc
                collected.sort((a, b) => {
                    const ta = a.date ? new Date(a.date).getTime() : 0;
                    const tb = b.date ? new Date(b.date).getTime() : 0;
                    return tb - ta;
                });
                setRows(collected);
            } catch (e) {
                console.error("Failed to fetch prospects/logs", e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // Load user's email provider preference
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await getMyInfo();
                if (cancelled) return;
                if (data && (data as any).emailProvider) {
                    setEmailProvider((data as any).emailProvider);
                }
            } catch (e) {
                console.warn("Failed to load email provider preference:", e);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const filtered = useMemo(() => {
        const qq = q.trim().toLowerCase();
        return rows.filter((r) => {
            if (typeFilter !== "all" && r.type !== typeFilter) return false;
            if (!qq) return true;
            return (
                String(r.prospectName || "").toLowerCase().includes(qq) ||
                String(r.subject || "").toLowerCase().includes(qq) ||
                String(r.message || "").toLowerCase().includes(qq) ||
                String(r.type || "").toLowerCase().includes(qq)
            );
        });
    }, [rows, q, typeFilter]);

    const types = Array.from(new Set(rows.map((r) => r.type))).filter(Boolean);

    const getTypeMeta = (t: string) => {
        const type = (t || "").toLowerCase();
        if (type.includes("email")) {
            const dir = type.includes("send") ? "Sent" : type.includes("receive") ? "Received" : "";
            return {
                label: `Email ${dir}`.trim(),
                icon: <Mail className="w-4 h-4" />,
                category: "email",
                color: "bg-surface-sunken text-ink-muted",
            };
        }
        if (type.includes("linkedin")) {
            const dir = type.includes("send") ? "Sent" : type.includes("receive") ? "Received" : "";
            return {
                label: `LinkedIn ${dir}`.trim(),
                icon: <Linkedin className="w-4 h-4" />,
                category: "linkedin",
                color: "bg-info-subtle text-info",
            };
        }
        if (type.includes("meeting") || type.includes("call")) {
            return {
                label: `Meeting`,
                icon: <Calendar className="w-4 h-4" />,
                category: "meeting",
                color: "bg-warning-subtle text-warning",
            };
        }
        return {
            label: t || "Event",
            icon: <ExternalLink className="w-4 h-4" />,
            category: "other",
            color: "bg-surface-sunken text-ink-muted",
        };
    };

    const openEmailSearch = (query: string) => {
        const url = generateEmailSearchUrl(emailProvider, query);
        if (url) {
            window.open(url, "_blank", "noopener noreferrer");
        } else {
            // Fallback for providers that don't support search
            alert("Email search is not supported for your email provider.");
        }
    };

    return (
        <div className="min-h-screen bg-canvas font-sans">
             <div className="max-w-6xl mx-auto px-4 sm:px-6">
             <ProspectHeatmap />
             </div>
            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
                <div className="flex items-center justify-between mb-3 gap-3">
                    <h1 className="font-sans text-2xl font-semibold tracking-tight text-ink">Activités</h1>
                    <div className="flex items-center gap-2">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-faint" />
                            <Input
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Search logs..."
                                className="pl-9 h-9 w-56"
                            />
                        </div>
                        <Select
                            value={typeFilter}
                            onChange={setTypeFilter}
                            options={[
                                { value: "all", label: "All types" },
                                ...types.map((t) => ({ value: t, label: t })),
                            ]}
                        />
                    </div>
                </div>

                <div className="rounded-2xl border border-border bg-surface overflow-hidden">
                    <div className="bg-surface-sunken border-b border-border px-3 py-2 text-xs text-ink-faint">{filtered.length} logs</div>
                    <div className="divide-y divide-border max-h-[70vh] overflow-auto">
                        {loading ? (
                            <div className="p-6 text-sm text-ink-muted">Loading...</div>
                        ) : filtered.length === 0 ? (
                            <div className="p-6 text-sm text-ink-muted">No logs found.</div>
                        ) : (
                            filtered.map((r) => {
                                const meta = getTypeMeta(r.type);
                                return (
                                    <div key={r.id} className="p-3 hover:bg-surface-sunken/60 transition-colors duration-150 flex items-start gap-3">
                                        <div className="flex-1">
                                            <div className="flex items-center justify-between gap-4">
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-9 h-9 flex items-center justify-center rounded-full ${meta.color}`}>{meta.icon}</div>
                                                    <div>
                                                        <div className="text-sm font-semibold text-ink">{r.prospectName}</div>
                                                        <div className="text-xs text-ink-muted">{meta.label}</div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {meta.category === "email" && r.prospectEmail ? (
                                                        <button
                                                            title="Search in Email"
                                                            onClick={() => openEmailSearch(r.prospectEmail!)}
                                                            className="px-2 py-1 rounded-lg border border-border-strong text-xs text-ink hover:bg-surface-sunken transition-colors duration-150 flex items-center gap-1"
                                                        >
                                                            <ExternalLink className="w-3 h-3" /> Search
                                                        </button>
                                                    ) : null}
                                                    {meta.category === "linkedin" && r.prospectLinkedin ? (
                                                        <button
                                                            title="Open LinkedIn"
                                                            onClick={() => window.open(r.prospectLinkedin, "_blank", "noopener noreferrer")}
                                                            className="px-2 py-1 rounded-lg border border-border-strong text-xs text-ink hover:bg-surface-sunken transition-colors duration-150 flex items-center gap-1"
                                                        >
                                                            <ExternalLink className="w-3 h-3" /> Open
                                                        </button>
                                                    ) : null}
                                                </div>
                                            </div>
                                            {r.subject && <div className="text-sm text-ink mt-2">Object: {r.subject}</div>}
                                            {r.message && <div className="mt-2 text-sm text-ink-muted whitespace-pre-wrap">{r.message}</div>}
                                            <div className="mt-3 flex items-center gap-2 text-xs text-ink-muted">
                                                <Badge tone="neutral">{formatDate(r.date)}</Badge>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
